import { useState } from "react";
import { PlannerHeader } from "./PlannerHeader";
import { PlannerOverview } from "./PlannerOverview";
import { PlannerWordManager } from "./PlannerWordManager";
import { PlannerWordReport } from "./PlannerWordReport";
import { PlannerRewards } from "./PlannerRewards";
import { PlannerProfile } from "./PlannerProfile";
import { PlannerSettings } from "./PlannerSettings";

const TABS = [
  { key: "overview", label: "Overview",   icon: "🏠" },
  { key: "words",    label: "Word Lists", icon: "📝" },
  { key: "report",   label: "Report",     icon: "📊" },
  { key: "rewards",  label: "Rewards",    icon: "🎁" },
  { key: "profile",  label: "My Profile", icon: "👤" },
  { key: "settings", label: "Settings",   icon: "⚙️" },
];

export function PlannerDashboard({ data, users, onDataChange, settings, onSettingsChange, onLogout }) {
  const [tab, setTab] = useState("overview");
  const children = users.filter(u => u.role === "child");
  const parent = users.find(u => u.role === "parent");
  const pendingCount = (data.requests || []).filter(r => r.status === "pending").length;

  return (
    <div className="paper-texture min-h-screen">
      {/* ── Header ── */}
      <PlannerHeader parent={parent} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row">
        {/* ── Mobile Nav: horizontal scroll pills ── */}
        <nav className="lg:hidden overflow-x-auto px-4 py-3 border-b border-stone-200 bg-stone-50/80">
          <div className="flex gap-2 min-w-max">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all relative
                  ${tab === t.key
                    ? "bg-amber-500 text-white shadow-sm"
                    : "bg-stone-200 text-stone-700 hover:bg-stone-300"
                  }`}
              >
                {t.label}
                {t.key === "rewards" && pendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </nav>

        {/* ── Desktop Sidebar: binder tabs ── */}
        <nav className="hidden lg:block w-56 py-8 pl-4 pr-2 flex-shrink-0">
          <div className="bg-stone-100 rounded-r-lg border-l-4 border-amber-400 shadow-md">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`binder-tab w-full text-left px-4 py-3 border-b border-stone-200 last:border-b-0 flex items-center gap-3 relative transition-all
                  ${tab === t.key
                    ? "active bg-paper-cream"
                    : "hover:bg-amber-50"
                  }`}
              >
                <span className="text-lg">{t.icon}</span>
                <span className="font-handwritten text-xl">{t.label}</span>
                {t.key === "rewards" && pendingCount > 0 && (
                  <span className="absolute right-3 top-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </nav>

        {/* ── Content Area ── */}
        <main className="flex-1 py-4 lg:py-8 px-4 lg:pr-4 lg:pl-6">
          {tab === "overview" && (
            <PlannerOverview
              data={data}
              users={users}
              children={children}
              onDataChange={onDataChange}
              onSwitchTab={setTab}
            />
          )}
          {tab === "words" && (
            <PlannerWordManager
              data={data}
              onDataChange={onDataChange}
              apiKey={settings.geminiKey || ""}
              geminiModel={settings.geminiModel || "gemini-1.5-flash"}
            />
          )}
          {tab === "report" && <PlannerWordReport data={data} users={users} />}
          {tab === "rewards" && (
            <PlannerRewards
              data={data}
              users={users}
              onDataChange={onDataChange}
              settings={settings}
            />
          )}
          {tab === "profile" && (
            <PlannerProfile
              user={parent}
              data={data}
              onDataChange={onDataChange}
            />
          )}
          {tab === "settings" && (
            <PlannerSettings
              settings={settings}
              onSettingsChange={onSettingsChange}
              users={users}
              data={data}
              onDataChange={onDataChange}
            />
          )}
        </main>
      </div>
    </div>
  );
}
